import type { ExpDisplayUpdate, ExpRateChartSnapshot, RealtimeExpRateMetric } from '@/domain/types'
import { EXP_RATE_WINDOW_MS } from './session-exp-history'

export const REALTIME_EXP_WINDOW_MS = EXP_RATE_WINDOW_MS
type Direction = RealtimeExpRateMetric['direction']

/** Compares the history calculator's shared 60s window; stores no source observations. */
export class SessionExpRateTracker {
  private direction: Direction = null
  reset(): void { this.direction = null }

  snapshot(recent: ExpRateChartSnapshot['recent'], average: number | null, sourceAverage: number | null,
    displayStatus: ExpDisplayUpdate['status'], running: boolean): RealtimeExpRateMetric {
    const empty = (status: RealtimeExpRateMetric['status']): RealtimeExpRateMetric => {
      this.direction = null
      return { value: null, delta: null, status, direction: null, windowMs: REALTIME_EXP_WINDOW_MS,
        windowStartAt: null, updatedAt: recent.at, approximate: false }
    }
    if (!running || average === null || !Number.isFinite(average)) return empty('unavailable')
    if (displayStatus === 'pending-transition' || displayStatus === 'unresolved-level') return empty('pending')
    if (recent.status !== 'ready') return empty(recent.status === 'paused' ? 'unavailable' : recent.status)
    const value = recent.value
    if (value === null || !Number.isFinite(value)) return empty('unavailable')
    const delta = value - average
    if (!Number.isFinite(delta)) return empty('unavailable')
    if (sourceAverage === null || !Number.isFinite(sourceAverage)) return empty('unavailable')
    const bias = Math.abs(sourceAverage - average)
    const next: Direction = delta > 0 ? 'up' : delta < 0 ? 'down' : null
    if (next !== this.direction) this.direction = null
    const threshold = Math.max(1, Math.abs(average) * (this.direction ? .02 : .04)) + bias
    const precision = Number.EPSILON * Math.max(1, Math.abs(value), Math.abs(average)) * 8
    if (this.direction ? Math.abs(delta) <= threshold + precision : Math.abs(delta) + precision < threshold) this.direction = null
    else this.direction = next
    return { value, delta, status: 'ready', direction: this.direction, windowMs: REALTIME_EXP_WINDOW_MS,
      windowStartAt: recent.windowStartAt ?? (recent.at === null ? null : recent.at - REALTIME_EXP_WINDOW_MS), updatedAt: recent.at,
      approximate: recent.approximate ?? false }
  }
}
